import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import api from "../../Api";

const ConfirmNewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = location.pathname.split("/")[3];

  // Override body background for this page
  useEffect(() => {
    const originalBackground = document.body.style.background;
    document.body.style.background = 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)';
    return () => {
      document.body.style.background = originalBackground;
    };
  }, []);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (!passwordRegex.test(formData.password)) {
      errors.password =
        "Min 8 chars with uppercase, lowercase, number & special character";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      try {
        await api.post(
          `/classBooking/api/auth/updatepassword?token=${token}`,
          { password: formData.password }
        );
        navigate("/en/login", { 
          state: { message: "Password updated successfully! Please login with your new password." } 
        });
      } catch (err) {
        setFormErrors(
          err.response?.data || { general: "Failed to update password. Please try again." }
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <PageContainer>
      <PasswordCard>
        <BackButton onClick={() => navigate(-1)}>← Back</BackButton>

        <HeaderSection>
          <IconWrapper>🔐</IconWrapper>
          <TitleText>Set New Password</TitleText>
          <SubText>Create a strong password for your account</SubText>
        </HeaderSection>

        <FormSection onSubmit={handleSubmit}>
          <InputGroup>
            <InputLabel>New Password</InputLabel>
            <InputWrapper $hasError={formErrors.password}>
              <InputIcon>🔒</InputIcon>
              <StyledInput
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <TogglePassword
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "🙈" : "👁️"}
              </TogglePassword>
            </InputWrapper>
            {formErrors.password && <ErrorText>{formErrors.password}</ErrorText>}
          </InputGroup>

          <InputGroup>
            <InputLabel>Confirm New Password</InputLabel>
            <InputWrapper $hasError={formErrors.confirmPassword}>
              <InputIcon>🔒</InputIcon>
              <StyledInput
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <TogglePassword
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "🙈" : "👁️"}
              </TogglePassword>
            </InputWrapper>
            {formErrors.confirmPassword && (
              <ErrorText>{formErrors.confirmPassword}</ErrorText>
            )}
          </InputGroup>

          {formErrors.general && <GeneralError>{formErrors.general}</GeneralError>}
          {formErrors.error && <GeneralError>{formErrors.error}</GeneralError>}

          <PasswordHint>
            <HintTitle>Password must contain:</HintTitle>
            <HintList>
              <HintItem $valid={formData.password.length >= 8}>
                At least 8 characters
              </HintItem>
              <HintItem $valid={/[A-Z]/.test(formData.password)}>
                One uppercase letter
              </HintItem>
              <HintItem $valid={/[a-z]/.test(formData.password)}>
                One lowercase letter
              </HintItem>
              <HintItem $valid={/\d/.test(formData.password)}>
                One number
              </HintItem>
              <HintItem $valid={/[@$!%*?&]/.test(formData.password)}>
                One special character (@$!%*?&)
              </HintItem>
            </HintList>
          </PasswordHint>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? <Spinner /> : "Update Password"}
          </SubmitButton>
        </FormSection>
      </PasswordCard>

      <Footer>
        <FooterText>
          Make sure to remember your new password
        </FooterText>
        <FooterCopyright>
          © {new Date().getFullYear()} Dynamic Management Group Limited
        </FooterCopyright>
      </Footer>
    </PageContainer>
  );
};

export default ConfirmNewPassword;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-bottom: 60px;
`;

const PasswordCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: none;
  border: none;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 28px;
  margin-top: 20px;
`;

const IconWrapper = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const TitleText = styled.h1`
  font-family: "Poppins", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const SubText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #888;
  margin: 0;
`;

const FormSection = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputLabel = styled.label`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #333;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: ${(props) => (props.$hasError ? "#fff5f5" : "#f8f9fa")};
  border: 2px solid ${(props) => (props.$hasError ? "#ff6b6b" : "transparent")};
  border-radius: 12px;
  transition: all 0.2s ease;

  &:focus-within {
    background: white;
    border-color: ${(props) => (props.$hasError ? "#ff6b6b" : "#1a73e8")};
    box-shadow: 0 0 0 4px
      ${(props) =>
        props.$hasError ? "rgba(255, 107, 107, 0.1)" : "rgba(26, 115, 232, 0.1)"};
  }
`;

const InputIcon = styled.span`
  font-size: 16px;
  opacity: 0.7;
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #333;
  outline: none;

  &::placeholder {
    color: #aaa;
  }
`;

const TogglePassword = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ErrorText = styled.span`
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  color: #ff6b6b;
  padding-left: 4px;
`;

const GeneralError = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  color: #ff6b6b;
  text-align: center;
  padding: 12px;
  background: #fff5f5;
  border-radius: 10px;
`;

const PasswordHint = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
`;

const HintTitle = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
`;

const HintList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HintItem = styled.li`
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  color: ${(props) => (props.$valid ? "#34a853" : "#999")};
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "${(props) => (props.$valid ? "✓" : "○")}";
    font-size: 10px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 12px;
  font-family: "Poppins", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Footer = styled.footer`
  margin-top: 24px;
  text-align: center;
`;

const FooterText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  color: #999;
  margin: 0 0 8px 0;
  max-width: 300px;
`;

const FooterCopyright = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 10px;
  color: #bbb;
  margin: 0;
`;
